import type { PanInfo } from 'motion/react'
import type {
  DropzoneInputProps,
  DropzoneRootProps,
  FileRejection,
  FileWithPath,
} from 'react-dropzone'
import { formatFileSize } from '~/utils/common'

export function _getInputProps<T extends DropzoneInputProps>(props?: T): T {
  console.warn('no-op')
  return props ?? ({} as T)
}

export function _getRootProps<T extends DropzoneRootProps>(props?: T): T {
  console.warn('no-op')
  return props ?? ({} as T)
}

export function _noOp() {
  console.warn('no-op')
}

export function composeErrorText({
  copy,
  fileRejections,
  isDragAccept,
  isDragActive,
  maxFileSize,
}: {
  copy: {
    dragErrorText: string
    fileSizeErrorText: string | undefined
    fileTypeErrorText: string
    multiErrorText: string
  }
  fileRejections: FileRejection[]
  isDragAccept: boolean
  isDragActive: boolean
  maxFileSize: number
}) {
  if ((isDragActive && !isDragAccept) || fileRejections.length <= 0) {
    return copy.dragErrorText
  }

  const multipleRejections = fileRejections.length > 1
  if (multipleRejections) {
    return copy.multiErrorText
  }

  const rejectionCode = fileRejections[0].errors[0].code
  const fileName = fileRejections[0].file.name
  const fileSize = fileRejections[0].file.size
  const fileExtension = fileName.substring(fileName.lastIndexOf('.') + 1)
  let errorText: string
  switch (rejectionCode) {
    case 'file-invalid-type': {
      const rejectedFileTypeText =
        fileExtension && fileExtension !== '' ? fileExtension : 'unknown'
      errorText = `${copy.fileTypeErrorText} - (${rejectedFileTypeText})`
      break
    }
    case 'file-too-large': {
      errorText =
        copy.fileSizeErrorText ??
        `The maximum file size allowed is ${formatFileSize(
          maxFileSize,
        )} (this file is ${formatFileSize(fileSize)})`
      break
    }
    default: {
      errorText = 'An unknown error occurred - please try again.'
      break
    }
  }

  return errorText
}

export function isDiff(
  a: FileWithPath[] | FileRejection[],
  b: FileWithPath[] | FileRejection[],
) {
  function isRejected(candidate: unknown): candidate is FileRejection {
    return (
      typeof candidate === 'object' && candidate !== null && 'file' in candidate
    )
  }

  if (a.length !== b.length) {
    return true
  }

  const sampleA = a.length > 0 ? a[0] : null
  const sampleB = b.length > 0 ? b[0] : null

  const isARejected = sampleA && isRejected(sampleA)
  const isBRejected = sampleB && isRejected(sampleB)

  if (
    isARejected !== null &&
    isBRejected !== null &&
    isARejected !== isBRejected
  ) {
    console.error('TypeError: Comparing FileWithPath[] to FileRejection[]')
    return false
  }

  return a.some((fileA: FileWithPath | FileRejection, index: number) => {
    const fileB = b[index]
    if (isRejected(fileA) && isRejected(fileB)) {
      return fileA.file.path !== fileB.file.path
    }

    if (!isRejected(fileA) && !isRejected(fileB)) {
      return fileA.path !== fileB.path
    }

    return false // Ensures no updates if there's an unexpected mismatch
  })
}

export function swapItems(
  info: PanInfo,
  index: number,
  files: FileWithPath[],
): FileWithPath[] | null {
  const indexDelta =
    Math.sign(info.offset.y) * (Math.abs(info.offset.y) > 30 ? 1 : 0)

  const indexTarget = index + indexDelta

  /** Prevents swapping when dragging the first item up or the last item down */
  const isIndexTargetOutOfBounds =
    indexTarget < 0 || indexTarget >= files.length
  if (isIndexTargetOutOfBounds) {
    return null
  }

  const _files = [...files]
  const item = _files[index]
  const itemAtTarget = _files[indexTarget]

  _files[index] = itemAtTarget
  _files[indexTarget] = item

  return _files
}

export function generateFileHash() {
  return (
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
  )
}
